if (!customElements.get('custom-blog-post-layout')) {
  customElements.define(
    'custom-blog-post-layout',
    class CustomBlogPostLayout extends HTMLElement {
      connectedCallback() {
        this.resetActiveState();
        this.content = this.querySelector('[data-blog-post-content]');
        this.toc = this.querySelector('[data-blog-post-toc]');
        this.tocList = this.querySelector('[data-blog-post-toc-list]');
        this.linksById = new Map();

        if (!this.content || !this.toc || !this.tocList) return;

        this.classList.remove('custom-blog-post__layout--toc-ready');
        this.toc.hidden = true;
        this.tocList.replaceChildren();
        this.linksById.clear();
        this.headings = [];

        const headings = this.getTocHeadings();

        if (!headings.length) {
          return;
        }

        const seenIds = new Set();

        headings.forEach((heading, index) => {
          const fallbackId = `section-${index + 1}`;
          const baseId = this.slugify(heading.textContent) || fallbackId;
          let headingId = heading.id || baseId;
          let suffix = 2;

          while (
            seenIds.has(headingId) ||
            (document.getElementById(headingId) && document.getElementById(headingId) !== heading)
          ) {
            headingId = `${baseId}-${suffix}`;
            suffix += 1;
          }

          seenIds.add(headingId);
          heading.id = headingId;

          const item = document.createElement('li');
          const link = document.createElement('a');

          link.href = `#${headingId}`;
          link.textContent = heading.textContent.trim();
          link.dataset.targetId = headingId;

          item.appendChild(link);
          this.tocList.appendChild(item);
          this.linksById.set(headingId, link);
        });

        this.headings = headings;
        this.toc.hidden = false;
        this.classList.add('custom-blog-post__layout--toc-ready');
        this.bindActiveState();
      }

      getTocHeadings() {
        const selectorsByPriority = ['h2', 'h3', 'h4'];

        for (const selector of selectorsByPriority) {
          const headings = Array.from(this.content.querySelectorAll(selector)).filter(
            (heading) => heading.textContent.trim().length > 0
          );

          if (headings.length) return headings;
        }

        return [];
      }

      slugify(value) {
        return (value || '')
          .toLowerCase()
          .trim()
          .replace(/['"]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      bindActiveState() {
        if (!this.headings?.length) return;

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              const visible = entries
                .filter((entry) => entry.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

              if (visible.length) {
                this.setActiveLink(visible[0].target.id);
                return;
              }

              this.updateActiveLinkFromScroll();
            },
            {
              rootMargin: '-20% 0px -60% 0px',
              threshold: [0, 1]
            }
          );

          this.headings.forEach((heading) => this.observer.observe(heading));
        }

        this.onScroll = this.updateActiveLinkFromScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateActiveLinkFromScroll();
      }

      updateActiveLinkFromScroll() {
        if (!this.headings?.length) return;

        const offset = 170;
        let activeHeading = this.headings[0];

        this.headings.forEach((heading) => {
          const top = heading.getBoundingClientRect().top;

          if (top - offset <= 0) {
            activeHeading = heading;
          }
        });

        this.setActiveLink(activeHeading.id);
      }

      setActiveLink(id) {
        if (!id) return;

        this.linksById.forEach((link, key) => {
          const isActive = key === id;
          link.classList.toggle('is-active', isActive);
          if (isActive) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }

      resetActiveState() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          this.onScroll = null;
        }
      }

      disconnectedCallback() {
        this.resetActiveState();
      }
    }
  );
}
